// Three
import * as THREE from 'three';

import { SHADERS, uvCrop, buildImageArray } from './GalleryItem';
import { ITEMS, CONFIG, generateLayout } from './layout';

// Batched WebGL gallery. Every image is one instance of a SINGLE
// InstancedBufferGeometry (1 draw call) sampled from one DataArrayTexture; the
// lone <video> is a second mesh on the same shader (2 draws total). Position,
// the infinite wrap, and the lens bend are all computed in the vertex shader
// from a few shared uniforms — so a per-frame update is ~4 uniform writes, zero
// per-item CPU. Each instance's base/size/crop is computed from the layout data
// (./layout.js) in applyLayout() — on first frame / resize / authoring — NO DOM.
//
// Authored transforms (intro timeline, #debug) live in `items[i].tf`; they are
// uploaded to per-instance attributes only while something is animating them
// (syncTf), so steady-state scroll uploads nothing.
const LAYER_SIZE = 1024; // px per texture-array layer (square, aspect-fit + crop)

export default class Gallery {
	constructor({ scene, sizes, galleryComponent }) {
		this.scene = scene;
		this.sizes = sizes;
		this.galleryComponent = galleryComponent; // DOM gallery: scroll + scale + bounds
		this.isReady = false;

		this.entries = []; // { item, mediaEl, isVideo, src, attrIndex }
		this.items = []; // authoring descriptors (one per item, ITEMS order)
		this.videoItem = null;
		// when set (authoring/#debug), the bake uses this fixed period instead of the
		// freshly computed one — so resizing a plane can't shift the recenter/wrap.
		this.lockedPeriod = null;

		// shared lens uniform (animated by GalleryControls' intro/GUI)
		this.uniforms = { uLensK: { value: 0 } };
	}

	createItems() {
		// Layout is data-driven (./layout.js), keyed by id — no DOM figures. The lone
		// video still needs a real <video> element for the VideoTexture, so grab the
		// hidden one in the pug.
		this.entries = ITEMS.map((item) => ({
			item,
			isVideo: !!item.isVideo,
			src: item.src,
			mediaEl: item.isVideo ? document.querySelector('.portfolio__video') : null,
		}));

		const imageSrcs = this.entries.filter((e) => !e.isVideo).map((e) => e.src);

		return buildImageArray(imageSrcs, LAYER_SIZE).then(({ texture, info }) => {
			this.array = texture;
			this.info = info;
			this.buildMeshes();
			this.isReady = true;
			this.applyLayout();
			this.update();
		});
	}

	buildMeshes() {
		const base = new THREE.PlaneGeometry(1, 1, 16, 16);

		// ---- instanced image batch ----
		let n = 0;
		this.entries.forEach((e) => {
			if (!e.isVideo) e.attrIndex = n++;
		});
		this.count = n;

		const geo = new THREE.InstancedBufferGeometry();
		geo.index = base.index;
		geo.setAttribute('position', base.attributes.position);
		geo.setAttribute('uv', base.attributes.uv);
		geo.instanceCount = n;

		const f2 = () => new THREE.InstancedBufferAttribute(new Float32Array(n * 2), 2);
		const f1 = () => new THREE.InstancedBufferAttribute(new Float32Array(n), 1);
		this.attr = {
			aBase: f2(),
			aSize: f2(),
			aUvRepeat: f2(),
			aUvOffset: f2(),
			aLayer: f1(),
			aOffset: f2(),
			aRot: f1(),
			aTfScale: f1(),
		};
		Object.keys(this.attr).forEach((k) => geo.setAttribute(k, this.attr[k]));
		// aTfScale defaults to 1 (no scale); the rest default to 0
		this.attr.aTfScale.array.fill(1);

		this.imageMaterial = new THREE.RawShaderMaterial({
			glslVersion: THREE.GLSL3,
			uniforms: {
				uArray: { value: this.array },
				uScroll: { value: new THREE.Vector2() },
				uScale: { value: 1 },
				uPeriod: { value: new THREE.Vector2(1, 1) },
				uLensK: this.uniforms.uLensK,
			},
			vertexShader: SHADERS.IMAGE_VERT,
			fragmentShader: SHADERS.IMAGE_FRAG,
			transparent: true,
			depthTest: false,
			depthWrite: false,
		});

		this.imageGeo = geo;
		this.imageMesh = new THREE.Mesh(geo, this.imageMaterial);
		this.imageMesh.frustumCulled = false;
		this.scene.add(this.imageMesh);

		// ---- single video plane (same shader, placement from uniforms) ----
		const videoEntry = this.entries.find((e) => e.isVideo);
		if (videoEntry && videoEntry.mediaEl) {
			const videoEl = videoEntry.mediaEl;
			// the hidden <video> is opacity:0; some browsers (Safari) won't autoplay
			// an "invisible" muted video, so kick it off explicitly.
			videoEl.muted = true;
			videoEl.playsInline = true;
			const p = videoEl.play();
			if (p && p.catch) p.catch(() => {});
			const tex = new THREE.VideoTexture(videoEl);
			tex.colorSpace = THREE.LinearSRGBColorSpace;
			this.videoTexture = tex;
			this.videoMaterial = new THREE.RawShaderMaterial({
				glslVersion: THREE.GLSL3,
				uniforms: {
					uMap: { value: tex },
					uBase: { value: new THREE.Vector2() },
					uSize: { value: new THREE.Vector2() },
					uUvRepeat: { value: new THREE.Vector2(1, 1) },
					uUvOffset: { value: new THREE.Vector2() },
					uOffset: { value: new THREE.Vector2() },
					uRot: { value: 0 },
					uTfScale: { value: 1 },
					uScroll: { value: new THREE.Vector2() },
					uScale: { value: 1 },
					uPeriod: { value: new THREE.Vector2(1, 1) },
					uLensK: this.uniforms.uLensK,
				},
				vertexShader: SHADERS.VIDEO_VERT,
				fragmentShader: SHADERS.VIDEO_FRAG,
				transparent: true,
				depthTest: false,
				depthWrite: false,
			});
			this.videoGeo = base; // reuse the same 1x1 subdivided quad
			this.videoMesh = new THREE.Mesh(base, this.videoMaterial);
			this.videoMesh.frustumCulled = false;
			this.scene.add(this.videoMesh);
			this.videoEntry = videoEntry;
		}

		// authoring descriptors, in ITEMS order (intro stagger relies on it)
		this.items = this.entries.map((e, i) => ({
			index: i,
			id: e.item.id,
			item: e.item, // layout entry (carries override, w/h, mode)
			isVideo: e.isVideo,
			attrIndex: e.attrIndex,
			tf: { x: 0, y: 0, z: 0, rotation: 0, scale: 1 },
			baseX: 0, // world center, set by applyLayout (used by intro + GUI)
			baseY: 0,
			boxW: 0, // plane box px (post-scale), for GUI hit-testing
			boxH: 0,
			sizes: this.sizes,
		}));
	}

	// Resize / first frame / after authoring: compute every plane's base/size/crop
	// from the layout data (./layout.js) — NO DOM reads. Design space (origin
	// top-left, +y down) is recentered on the period and Y-flipped into world space
	// (origin = screen center, +y up), then scaled by the gallery's responsive scale.
	applyLayout() {
		if (!this.isReady) return;
		const scale = this.galleryComponent.scale || 1;
		const { placements, period: generated } = generateLayout(ITEMS, CONFIG);
		// authoring pins the period so an edit to one plane never moves the others
		const period = this.lockedPeriod || generated;
		this.period = period; // design px (the GUI hit-test reads this)

		this.entries.forEach((e, i) => {
			const item = this.items[i];
			const p = placements.get(item.id);
			if (!p) return;

			const baseX = (p.x - period.w / 2) * scale; // world center
			const baseY = -(p.y - period.h / 2) * scale;
			const boxW = p.w * scale;
			const boxH = p.h * scale;

			item.baseX = baseX;
			item.baseY = baseY;
			item.boxW = boxW;
			item.boxH = boxH;
			// authored rotation/scale ride the tf channel (the only rot/scale path in
			// the shader); the intro targets them and the GUI edits them — not here.

			if (e.isVideo) {
				const v = e.mediaEl;
				const aspect = (v && v.videoWidth / v.videoHeight) || boxW / boxH;
				const crop = uvCrop(e.item.mode, boxW, boxH, aspect, e.item.objectPosition);
				const u = this.videoMaterial.uniforms;
				u.uBase.value.set(baseX, baseY);
				u.uSize.value.set(crop.width, crop.height);
				u.uUvRepeat.value.set(crop.repeat[0], crop.repeat[1]);
				u.uUvOffset.value.set(crop.offset[0], crop.offset[1]);
				return;
			}

			const meta = this.info.get(e.src) || { layer: 0, fit: { repeat: [1, 1], offset: [0, 0] }, aspect: 1 };
			const crop = uvCrop(e.item.mode, boxW, boxH, meta.aspect, e.item.objectPosition);

			// compose: layerUv = fit.offset + (crop.offset + quadUv*crop.repeat)*fit.repeat
			const fr = meta.fit.repeat;
			const fo = meta.fit.offset;
			const repeat = [crop.repeat[0] * fr[0], crop.repeat[1] * fr[1]];
			const offset = [fo[0] + crop.offset[0] * fr[0], fo[1] + crop.offset[1] * fr[1]];

			const a = e.attrIndex;
			this.attr.aBase.array[a * 2] = baseX;
			this.attr.aBase.array[a * 2 + 1] = baseY;
			this.attr.aSize.array[a * 2] = crop.width;
			this.attr.aSize.array[a * 2 + 1] = crop.height;
			this.attr.aUvRepeat.array[a * 2] = repeat[0];
			this.attr.aUvRepeat.array[a * 2 + 1] = repeat[1];
			this.attr.aUvOffset.array[a * 2] = offset[0];
			this.attr.aUvOffset.array[a * 2 + 1] = offset[1];
			this.attr.aLayer.array[a] = meta.layer;
		});

		['aBase', 'aSize', 'aUvRepeat', 'aUvOffset', 'aLayer'].forEach((k) => {
			this.attr[k].needsUpdate = true;
		});

		// tiling period = the layout's field extent (world px) so copies tile seamlessly
		this.imageMaterial.uniforms.uPeriod.value.set(period.w * scale, period.h * scale);
		if (this.videoMaterial) {
			this.videoMaterial.uniforms.uPeriod.value.set(period.w * scale, period.h * scale);
		}
	}

	// Per frame: push the shared scroll/scale uniforms. Pure uniform writes.
	update() {
		if (!this.isReady) return;
		const { scroll, scale } = this.galleryComponent;
		const iu = this.imageMaterial.uniforms;
		iu.uScroll.value.set(scroll.x, scroll.y);
		iu.uScale.value = scale;
		if (this.videoMaterial) {
			const vu = this.videoMaterial.uniforms;
			vu.uScroll.value.set(scroll.x, scroll.y);
			vu.uScale.value = scale;
		}
	}

	// Push authored tf (intro / #debug) into per-instance attributes + video
	// uniforms. Called only while a timeline or #debug is live.
	syncTf() {
		if (!this.isReady) return;
		this.items.forEach((item) => {
			const tf = item.tf;
			if (item.isVideo) {
				if (!this.videoMaterial) return;
				const u = this.videoMaterial.uniforms;
				u.uOffset.value.set(tf.x, tf.y);
				u.uRot.value = tf.rotation;
				u.uTfScale.value = tf.scale;
				return;
			}
			const a = item.attrIndex;
			this.attr.aOffset.array[a * 2] = tf.x;
			this.attr.aOffset.array[a * 2 + 1] = tf.y;
			this.attr.aRot.array[a] = tf.rotation;
			this.attr.aTfScale.array[a] = tf.scale;
		});
		this.attr.aOffset.needsUpdate = true;
		this.attr.aRot.needsUpdate = true;
		this.attr.aTfScale.needsUpdate = true;
	}

	// Viewport-intersection test for the video plane — replicates the GPU wrap in
	// JS so render-on-demand knows when the (continuously-redrawn) video shows.
	isVideoOnScreen() {
		if (!this.videoMaterial) return false;
		const { scroll, scale } = this.galleryComponent;
		const u = this.videoMaterial.uniforms;
		const P = u.uPeriod.value;
		const base = u.uBase.value;
		const size = u.uSize.value;
		const wrap = (v, p) => (p ? ((((v + p / 2) % p) + p) % p) - p / 2 : v);
		const cx = wrap(base.x + scroll.x * scale, P.x); // world center
		const cy = wrap(base.y - scroll.y * scale, P.y);
		const sx = cx + this.sizes.width / 2; // back to screen
		const sy = this.sizes.height / 2 - cy;
		return (
			sx + size.x / 2 > 0 &&
			sx - size.x / 2 < this.sizes.width &&
			sy + size.y / 2 > 0 &&
			sy - size.y / 2 < this.sizes.height
		);
	}

	onResize(sizes) {
		this.sizes = sizes;
		// re-read + re-seed happens on the next Experience.update (via _dirty)
	}

	destroy() {
		if (this.imageMesh) {
			this.scene.remove(this.imageMesh);
			this.imageGeo.dispose();
			this.imageMaterial.dispose();
		}
		if (this.videoMesh) {
			this.scene.remove(this.videoMesh);
			this.videoGeo.dispose();
			this.videoMaterial.dispose();
			this.videoTexture.dispose();
		}
		if (this.array) this.array.dispose();
		this.entries = [];
		this.items = [];
		this.isReady = false;
	}
}
